import produce from 'immer';
import {useEffect, useState} from 'react';

import {normalizeNameForUniqueness} from 'procedures/design/flowchartManagementUtils';
import {STEP_NAME_PLACEHOLDER} from 'procedures/design/step-config/StepConfigConstants';
import {collectAllStepNames, findStep} from 'procedures/design/step-config/stepManagementUtils';
import {Method, ProcedureStepGroup} from 'procedures/ProcedureDomainTypes';
import {ProcedurePageContext} from 'procedures/ProcedurePageContext';
import {isEnterKey, isEscapeKey} from 'util/Keyboard';
import {ElementKeyboardEvent} from 'util/Types';

function normalizeStepName(draftName: string, thisStepCid: string, method: Method): string {
  const namesAlreadyInUse = collectAllStepNames(method.stepGroups, thisStepCid);
  return normalizeNameForUniqueness(draftName, namesAlreadyInUse, STEP_NAME_PLACEHOLDER);
}

interface DraftStateEditorSetup {
  method: Method;
  currentlyEditingStepCid: string | null;
  onChangeSteps: (stepGroups: Array<ProcedureStepGroup>) => void;
}

export interface DraftStateEditorInfo {
  draftName: string;
  setDraftName: (draftName: string) => void;
  storeDraftName: (stepCid: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  handleEnterAndEscape: (e: ElementKeyboardEvent) => void;
}

/**
 * Manages the step name text editor(s). While the user has a text editor focused, we block
 * autosaves, since we don't want to persist a draft name. Once the user finishes editing, we
 * can unblock autosaves, and normalize the name to ensure uniqueness.
 *
 * Note that the user can edit step names in two places. Using this tool in both places ensures
 * that they are correctly synchronized.
 */
export default function useStepNameDraftStateEditor(params: DraftStateEditorSetup): DraftStateEditorInfo {
  const initialName =
    (params.currentlyEditingStepCid
      ? findStep(params.method.stepGroups, params.currentlyEditingStepCid)
      : null
    )?.name ?? '';

  const [draftName, setDraftName] = useState<string>(initialName);
  const [initialDraftName, setInitialDraftName] = useState<string>(initialName);
  const {setShouldPauseAutosave} = ProcedurePageContext.useContext();

  const storeDraftName = (stepCid: string): void => {
    const updatedStepGroups = produce(params.method.stepGroups, (draftStepGroups) => {
      const step = findStep(draftStepGroups, stepCid)!;
      step.name = normalizeStepName(draftName, stepCid, params.method);
      setDraftName(step.name);
    });
    params.onChangeSteps(updatedStepGroups);

    setShouldPauseAutosave(false);
  };

  const abandonDraft = (): void => {
    setDraftName(initialDraftName);
  };

  const onFocus = (): void => {
    setShouldPauseAutosave(true);
  };
  const onBlur = (): void => {
    if (params.currentlyEditingStepCid != null) {
      storeDraftName(params.currentlyEditingStepCid);
    }
    setShouldPauseAutosave(false);
  };

  // Install this in the `onKeyDown` handler, for correct processing of ENTER and ESC
  const handleEnterAndEscape = (e: ElementKeyboardEvent): void => {
    const isEsc = isEscapeKey(e);
    if (isEnterKey(e) || isEsc) {
      if (isEsc) {
        abandonDraft();
      } else if (params.currentlyEditingStepCid != null) {
        storeDraftName(params.currentlyEditingStepCid);
      }
      e.preventDefault();

      // React will reuse the event object, so we cache the element we want to blur
      const el = e.currentTarget;
      setTimeout(() => {
        el.blur();
      });
    }
  };

  // When the selected step changes, update our draft state
  useEffect(() => {
    let newDraftName: string;
    if (params.currentlyEditingStepCid == null) {
      newDraftName = '';
    } else {
      const step = findStep(params.method.stepGroups, params.currentlyEditingStepCid);
      newDraftName = step?.name || '';
    }
    setDraftName(newDraftName);
    setInitialDraftName(newDraftName);
  }, [params.currentlyEditingStepCid, params.method.stepGroups]);

  // Cleanup on unmount. This would be disastrous.
  useEffect(
    () => () => {
      setShouldPauseAutosave(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return {
    draftName,
    setDraftName,
    storeDraftName,
    onFocus,
    onBlur,
    handleEnterAndEscape,
  };
}
